import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, computed, effect, inject, input, output } from '@angular/core';

import { ModalPopup } from '../../../../../shared/components/modal-popup/modal-popup';
import { SwitchableCompanyView } from '../../../../../models/company-switcher.model';

/** One side of the switch preview: the entity being left or entered. */
export interface SwitchCard {
  name: string;
  subtitle?: string;
  imageUrl?: string | null;
}

/**
 * "Confirm Company Switch" dialog — shows the entity being left beside the one
 * being entered, so the user sees exactly what is changing before committing.
 *
 * Also used for the header role (user group) switch: pass `title`, `message`
 * and `fromCard` / `toCard` instead of the company inputs.
 *
 * Presentational only: it performs no API call and holds no switch state. The
 * in-flight flag arrives as an input, which keeps this component decoupled from
 * the switch implementation.
 */
@Component({
  selector: 'app-confirm-switch-modal',
  standalone: true,
  imports: [CommonModule, ModalPopup],
  templateUrl: './confirm-switch-modal.html',
  styleUrl: './confirm-switch-modal.scss',
})
export class ConfirmSwitchModal {
  /** Company being left. */
  readonly currentCompany = input<SwitchableCompanyView | null>(null);
  /** Company being entered. */
  readonly targetCompany = input<SwitchableCompanyView | null>(null);
  /** Generic cards; when set they replace the company cards above. */
  readonly fromCard = input<SwitchCard | null>(null);
  readonly toCard = input<SwitchCard | null>(null);
  readonly title = input<string>('Confirm Company Switch');
  readonly message = input<string>('Are you sure you want to switch the company?');
  /** Round avatars (people / roles) instead of square company logos. */
  readonly roundImage = input<boolean>(false);
  readonly isOpen = input<boolean>(false);
  /** True while the switch request is in flight. */
  readonly isSwitching = input<boolean>(false);

  readonly confirmSwitch = output<void>();
  readonly cancelSwitch = output<void>();

  readonly fromView = computed<SwitchCard | null>(
    () => this.fromCard() ?? this.companyCard(this.currentCompany()),
  );
  readonly toView = computed<SwitchCard | null>(
    () => this.toCard() ?? this.companyCard(this.targetCompany()),
  );

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  /** Element that had focus before the dialog opened, so it can be restored. */
  private previouslyFocused: HTMLElement | null = null;

  constructor() {
    // Keyboard and screen-reader users would otherwise be left behind in the
    // header while the dialog is on screen.
    effect(() => {
      if (this.isOpen()) {
        this.previouslyFocused = document.activeElement as HTMLElement | null;
        // Wait for the dialog to render before reaching into it.
        setTimeout(() => this.focusConfirmButton(), 0);
      } else if (this.previouslyFocused) {
        this.previouslyFocused.focus?.();
        this.previouslyFocused = null;
      }
    });
  }

  private focusConfirmButton(): void {
    const confirmButton =
      this.elementRef.nativeElement.querySelector<HTMLElement>('.confirm-btn');
    confirmButton?.focus?.();
  }

  /** Keeps Tab inside the dialog while it is open. */
  @HostListener('document:keydown.tab', ['$event'])
  onTab(event: Event): void {
    if (!this.isOpen() || !(event instanceof KeyboardEvent)) return;

    const focusable = this.elementRef.nativeElement.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Company as a card; location is "Dallas, Texas" — only the parts that exist. */
  private companyCard(company: SwitchableCompanyView | null): SwitchCard | null {
    if (!company) return null;
    return {
      name: company.company_name,
      subtitle: [company.city_name, company.state_name].filter(Boolean).join(', '),
      imageUrl: company.logoUrl,
    };
  }

  initialOf(card: SwitchCard | null): string {
    return card?.name?.charAt(0)?.toUpperCase() || 'C';
  }

  onConfirm(): void {
    // Guard against a double click firing two switch requests.
    if (this.isSwitching()) return;
    this.confirmSwitch.emit();
  }

  onDismiss(): void {
    // A switch in flight must not be abandoned half-way — the token may already
    // have been reissued by the time the user changes their mind.
    if (this.isSwitching()) return;
    this.cancelSwitch.emit();
  }

  // ModalPopup handles backdrop and close-button dismissal but has no key
  // handling, so Escape is wired here rather than modifying the shared component.
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) this.onDismiss();
  }
}
